import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  CircleDashed,
  Plus,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { formatDate } from "../../lib/format";
import type {
  BusinessCenterCompanyRecord,
  BusinessCenterRoleKey,
  BusinessCenterUserRecord,
  BusinessCenterWorkspaceFeatureFlag,
  BusinessCenterWorkspaceKey,
  BusinessCenterWorkspaceNavigationItem,
  BusinessCenterPermissionMatrix,
  BusinessCenterWorkspaceProvisioningStatus,
  BusinessCenterWorkspaceRecord,
  BusinessCenterWorkspaceStatus,
  BusinessCenterWorkspaceType,
} from "../../lib/types";
import { loadBusinessCenterBrandingProfiles, refreshBusinessCenterBrandingProfiles, resolveBusinessCenterBrandingProfile } from "./businessCenterBrandingData";
import { loadBusinessCenterCompanies, loadBusinessCenterCompanyUsers, refreshBusinessCenterCompanies, refreshBusinessCenterCompanyUsers } from "./businessCenterCompanyUsersData";
import { loadBusinessCenterOnboardingRecords, refreshBusinessCenterOnboardingRecords } from "./businessCenterOnboardingData";
import {
  buildBusinessCenterWorkspaceProvisioningDashboard,
  createBusinessCenterWorkspaceRecord,
  loadBusinessCenterWorkspaceProvisioningRecords,
  loadBusinessCenterWorkspaceTemplates,
  setBusinessCenterWorkspaceStatus,
  upsertBusinessCenterWorkspaceProvisioningRecord,
  workspaceProvisioningStatusOptions,
  workspaceStatusOptions,
} from "./businessCenterWorkspaceProvisioningData";

type WorkspaceMode = "create" | "update";

interface WorkspaceFormState {
  companyId: string;
  workspaceKey: BusinessCenterWorkspaceKey;
  workspaceName: string;
  workspaceLabel: string;
  workspaceType: BusinessCenterWorkspaceType;
  brand: string;
  status: BusinessCenterWorkspaceStatus;
  provisioningStatus: BusinessCenterWorkspaceProvisioningStatus;
  portalStatus: BusinessCenterWorkspaceRecord["portalStatus"];
  ownerUserId: string;
  assignedUserIdsText: string;
  teamAssignment: string;
  modulesText: string;
  primaryColor: string;
  accentColor: string;
  logo: string;
  contactEmail: string;
  contactPhone: string;
  domain: string;
  portalDomain: string;
  portalUrl: string;
  permissionsNotes: string;
  roleKeys: BusinessCenterRoleKey[];
  featureFlags: BusinessCenterWorkspaceFeatureFlag[];
  navigation: BusinessCenterWorkspaceNavigationItem[];
  checklist: BusinessCenterWorkspaceRecord["checklist"];
  notes: string;
}

const defaultChecklist: BusinessCenterWorkspaceRecord["checklist"] = {
  companyLinked: true,
  brandingMapped: false,
  portalActivated: false,
  navigationLoaded: true,
  permissionsMapped: true,
  usersAssigned: false,
  reportingEnabled: true,
};

const roleOptions: Array<WorkspaceFormState["roleKeys"][number]> = ["admin", "manager", "team_member", "client", "viewer"];

export function WorkspaceProvisioningCenterPage() {
  const [companies, setCompanies] = useState<BusinessCenterCompanyRecord[]>([]);
  const [users, setUsers] = useState<BusinessCenterUserRecord[]>([]);
  const [workspaces, setWorkspaces] = useState<BusinessCenterWorkspaceRecord[]>([]);
  const [brandingProfiles, setBrandingProfiles] = useState(loadBusinessCenterBrandingProfiles());
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<BusinessCenterWorkspaceKey>("custom");
  const [query, setQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<WorkspaceMode>("create");
  const [form, setForm] = useState<WorkspaceFormState>(createEmptyWorkspaceForm());
  const templateCatalog = loadBusinessCenterWorkspaceTemplates();

  useBusinessCenterOSBranding(
    "Workspace Provisioning Center",
    "Shared workspace provisioning engine for Business Center OS companies, templates, branding, assignments, and navigation.",
  );

  useEffect(() => {
    void loadWorkspace();
  }, []);

  const dashboard = useMemo(() => buildBusinessCenterWorkspaceProvisioningDashboard(workspaces, companies, users), [companies, users, workspaces]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) || null,
    [selectedWorkspaceId, workspaces],
  );

  const selectedTemplate = useMemo(
    () => templateCatalog.find((template) => template.key === form.workspaceKey) || templateCatalog[0],
    [form.workspaceKey, templateCatalog],
  );

  const filteredWorkspaces = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return workspaces.filter((workspace) => {
      const matchesCompany = companyFilter === "all" || workspace.companyId === companyFilter;
      const matchesQuery =
        !normalized ||
        `${workspace.companyName} ${workspace.workspaceName} ${workspace.workspaceLabel} ${workspace.workspaceKey} ${workspace.status} ${workspace.provisioningStatus} ${workspace.teamAssignment}`
          .toLowerCase()
          .includes(normalized);
      return matchesCompany && matchesQuery;
    });
  }, [companyFilter, query, workspaces]);

  useEffect(() => {
    if (!selectedWorkspaceId && filteredWorkspaces[0]) {
      setSelectedWorkspaceId(filteredWorkspaces[0].id);
    }
  }, [filteredWorkspaces, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspace) {
      setMode("update");
      setSelectedTemplateKey(selectedWorkspace.workspaceKey);
      setForm(buildFormFromWorkspace(selectedWorkspace));
      return;
    }
    if (!selectedWorkspaceId) {
      setMode("create");
      const defaultCompany = companies.find((company) => company.companyType === "parent") || companies[0] || null;
      const template = loadBusinessCenterWorkspaceTemplates().find((item) => item.key === selectedTemplateKey) || loadBusinessCenterWorkspaceTemplates()[0];
      setForm(buildFormFromTemplate(template, defaultCompany, brandingProfiles, users));
    }
  }, [brandingProfiles, companies, selectedTemplateKey, selectedWorkspace, selectedWorkspaceId, users]);

  async function loadWorkspace() {
    setLoading(true);
    setError("");
    try {
      const [nextCompanies, nextUsers, nextBrandingProfiles] = await Promise.all([
        refreshBusinessCenterCompanies().catch(() => loadBusinessCenterCompanies()),
        refreshBusinessCenterCompanyUsers().catch(() => loadBusinessCenterCompanyUsers()),
        refreshBusinessCenterBrandingProfiles().catch(() => loadBusinessCenterBrandingProfiles()),
      ]);
      const nextWorkspaces = await loadBusinessCenterWorkspaceProvisioningRecords();
      const nextOnboarding = await refreshBusinessCenterOnboardingRecords().catch(() => loadBusinessCenterOnboardingRecords());
      setCompanies(nextCompanies);
      setUsers(nextUsers);
      setWorkspaces(nextWorkspaces);
      setBrandingProfiles(nextBrandingProfiles);
      void nextOnboarding;
      setSelectedWorkspaceId((current) => current || nextWorkspaces[0]?.id || "");
      if (!selectedWorkspaceId && nextWorkspaces[0]) {
        setMode("update");
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load workspace provisioning data.");
    } finally {
      setLoading(false);
    }
  }

  function updateForm<K extends keyof WorkspaceFormState>(key: K, value: WorkspaceFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleRole(role: WorkspaceFormState["roleKeys"][number]) {
    setForm((current) => ({
      ...current,
      roleKeys: current.roleKeys.includes(role) ? current.roleKeys.filter((item) => item !== role) : [...current.roleKeys, role],
    }));
  }

  function toggleChecklist(key: keyof WorkspaceFormState["checklist"]) {
    setForm((current) => ({ ...current, checklist: { ...current.checklist, [key]: !current.checklist[key] } }));
  }

  function toggleFeatureFlag(key: string) {
    setForm((current) => ({
      ...current,
      featureFlags: current.featureFlags.some((flag) => flag.key === key)
        ? current.featureFlags.map((flag) => (flag.key === key ? { ...flag, enabled: !flag.enabled } : flag))
        : [
            ...current.featureFlags,
            ...(selectedTemplate.defaultFeatureFlags.find((flag) => flag.key === key) ? [{ ...selectedTemplate.defaultFeatureFlags.find((flag) => flag.key === key)!, enabled: true }] : []),
          ],
    }));
  }

  function openTemplate(templateKey: BusinessCenterWorkspaceKey) {
    const template = loadBusinessCenterWorkspaceTemplates().find((item) => item.key === templateKey) || loadBusinessCenterWorkspaceTemplates()[0];
    const company = companies.find((item) => item.companyType === "parent") || companies[0] || null;
    setSelectedWorkspaceId("");
    setSelectedTemplateKey(template.key);
    setMode("create");
    setForm(buildFormFromTemplate(template, company, brandingProfiles, users));
  }

  function loadWorkspaceIntoForm(workspace: BusinessCenterWorkspaceRecord) {
    setSelectedWorkspaceId(workspace.id);
    setSelectedTemplateKey(workspace.workspaceKey);
    setMode("update");
    setForm(buildFormFromWorkspace(workspace));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const company = companies.find((item) => item.id === form.companyId) || companies[0] || null;
      if (!company) throw new Error("No company is available for provisioning.");
      const template = loadBusinessCenterWorkspaceTemplates().find((item) => item.key === form.workspaceKey) || loadBusinessCenterWorkspaceTemplates()[0];
      const brandingProfile = resolveBusinessCenterBrandingProfile(company.id, company.companySlug, company.companyName, company.brand);
      const record = createBusinessCenterWorkspaceRecord({
        id: selectedWorkspace?.id || `workspace-${company.companySlug}-${form.workspaceKey}`,
        companyId: company.id,
        companyName: company.companyName,
        companySlug: company.companySlug,
        parentCompanyId: company.parentCompanyId || null,
        workspaceKey: template.key,
        workspaceName: form.workspaceName.trim() || template.label,
        workspaceLabel: form.workspaceLabel.trim() || template.label,
        workspaceType: form.workspaceType,
        brand: form.brand.trim() || company.brand,
        status: form.status,
        provisioningStatus: form.provisioningStatus,
        portalStatus: form.portalStatus,
        portalUrl: form.portalUrl.trim() || buildPortalUrl(company.companySlug, template.key),
        ownerUserId: form.ownerUserId || null,
        assignedUserIds: splitList(form.assignedUserIdsText),
        assignmentUsers: users.filter((user) => splitList(form.assignedUserIdsText).includes(user.id)),
        teamAssignment: form.teamAssignment.trim(),
        modules: splitList(form.modulesText),
        navigation: selectedWorkspace?.navigation || template.defaultNavigation,
        featureFlags: form.featureFlags,
        permissions: {
          roleKeys: form.roleKeys,
          notes: form.permissionsNotes.trim(),
        },
        branding: {
          companyName: form.workspaceLabel.trim() || company.companyName,
          logo: form.logo.trim(),
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          contactEmail: form.contactEmail.trim() || company.companyName,
          contactPhone: form.contactPhone.trim(),
          domain: form.domain.trim() || `${company.companySlug}.com`,
          portalDomain: form.portalDomain.trim() || `portal.${company.companySlug}.com`,
        },
        checklist: form.checklist,
        sourceOnboardingId: brandingProfile?.sourceOnboardingId || null,
        sourceBrandingProfileId: brandingProfile?.id || null,
        notes: form.notes.trim(),
        createdAt: selectedWorkspace?.createdAt,
        updatedAt: selectedWorkspace?.updatedAt,
      });
      await upsertBusinessCenterWorkspaceProvisioningRecord(record);
      setMessage(`${record.workspaceLabel} has been ${selectedWorkspace ? "updated" : "provisioned"} for ${company.companyName}.`);
      setSelectedWorkspaceId(record.id);
      await loadWorkspace();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to provision the workspace.");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusAction(status: BusinessCenterWorkspaceStatus) {
    if (!selectedWorkspace) return;
    const note =
      status === "Active"
        ? "Activated from the workspace provisioning center."
        : status === "Paused"
          ? "Paused from the workspace provisioning center."
          : "Archived from the workspace provisioning center.";
    await setBusinessCenterWorkspaceStatus(selectedWorkspace.id, status, note);
    setMessage(`${selectedWorkspace.workspaceLabel} moved to ${status}.`);
    await loadWorkspace();
    setSelectedWorkspaceId(selectedWorkspace.id);
  }

  return (
    <BusinessCenterOSAdminShell
      title="Workspace Provisioning Center"
      description="Provision workspaces across companies, brands, and portals from one shared engine for templates, company linkage, portal activation, navigation blueprints, assignment coverage, and permission scaffolding."
      actions={
        <>
          <button
            type="button"
            onClick={() => openTemplate(selectedTemplateKey)}
            className="business-center-os-button inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]"
          >
            <Plus className="size-4" aria-hidden="true" />
            New workspace
          </button>
          <Link
            to="/admin/client-onboarding-center"
            className="business-center-os-button business-center-os-button--secondary inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]"
          >
            <ArrowRight className="size-4" aria-hidden="true" />
            Open onboarding
          </Link>
        </>
      }
      statusItems={[
        { label: "Workspaces", value: dashboard.totalWorkspaces },
        { label: "Active", value: dashboard.activeWorkspaces },
        { label: "Provisioning", value: dashboard.provisioningWorkspaces },
        { label: "Portal ready", value: dashboard.portalReadyWorkspaces },
      ]}
    >

      <section className="px-4 pb-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Branding ready" value={dashboard.brandingReadyWorkspaces} note="Workspaces with mapped branding surfaces." />
          <MetricCard label="User assignments" value={dashboard.userAssignments} note="Provisioned users scoped into workspaces." />
          <MetricCard label="Permission profiles" value={dashboard.permissionProfiles} note="Role profiles seeded across the workspace layer." />
          <MetricCard label="Companies loaded" value={companies.length} note="Parent, client, and future companies in scope." />
        </div>
      </section>

      <section className="px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[.9fr_1.1fr]">
          <div className="grid gap-6">
            <article className="business-center-os-card rounded-3xl p-5">
              <SectionHeading
                eyebrow="Workspace templates"
                title="Blueprint library"
                description="Choose a template to provision the correct module set, portal map, and navigation shell."
              />
              <div className="mt-5 grid gap-3">
                {templateCatalog.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => openTemplate(template.key)}
                    className={`grid gap-3 rounded-3xl border p-4 text-left transition ${
                      template.key === selectedTemplateKey && !selectedWorkspaceId
                        ? "border-[rgba(34,197,94,.42)] bg-[rgba(34,197,94,.08)]"
                        : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid gap-1">
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">{template.workspaceType}</p>
                        <h3 className="text-lg font-black text-white">{template.label}</h3>
                      </div>
                      <span className="rounded-full border border-[rgba(34,197,94,.18)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#86EFAC]">
                        {template.key}
                      </span>
                    </div>
                    <p className="text-sm font-semibold leading-6 text-[#C9D6DF]">{template.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {template.defaultModules.slice(0, 4).map((module) => (
                        <span key={module} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold text-[#D9E6F2]">
                          {module}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </article>

            <article className="business-center-os-card rounded-3xl p-5">
              <SectionHeading
                eyebrow="Workspace registry"
                title="Provisioned records"
                description="Search company-scoped workspace records and open the one you want to review or update."
              />
              <div className="mt-5 grid gap-3">
                <div className="grid gap-3 md:grid-cols-[1.2fr_.8fr]">
                  <label className="grid gap-2">
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">Search</span>
                    <div className="business-center-os-input flex items-center gap-2">
                      <Search className="size-4 text-[#8AA0B8]" aria-hidden="true" />
                      <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        className="w-full bg-transparent outline-none"
                        placeholder="Company, workspace, status, assignment..."
                      />
                    </div>
                  </label>
                  <label className="grid gap-2">
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">Company</span>
                    <select
                      value={companyFilter}
                      onChange={(event) => setCompanyFilter(event.target.value)}
                      className="business-center-os-input"
                    >
                      <option value="all">All companies</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="grid gap-3">
                  {filteredWorkspaces.map((workspace) => {
                    const active = selectedWorkspaceId === workspace.id;
                    return (
                      <button
                        key={workspace.id}
                        type="button"
                        onClick={() => loadWorkspaceIntoForm(workspace)}
                        className={`rounded-3xl border p-4 text-left transition ${
                          active ? "border-[rgba(34,197,94,.44)] bg-[rgba(34,197,94,.08)]" : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="grid gap-1">
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#86EFAC]">{workspace.workspaceKey}</p>
                            <h3 className="text-lg font-black text-white">{workspace.workspaceLabel}</h3>
                            <p className="text-sm font-semibold text-[#C9D6DF]">{workspace.companyName}</p>
                          </div>
                          <div className="grid justify-items-end gap-1 text-right">
                            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-[#D9E6F2]">
                              {workspace.status}
                            </span>
                            <span className="text-xs font-semibold text-[#8AA0B8]">{workspace.provisioningStatus}</span>
                          </div>
                        </div>
                        <p className="mt-3 text-sm leading-6 text-[#C9D6DF]">{workspace.teamAssignment || "No team assignment yet."}</p>
                      </button>
                    );
                  })}
                  {filteredWorkspaces.length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-5 text-sm font-semibold text-[#C9D6DF]">
                      No workspace records match the current filter.
                    </div>
                  ) : null}
                </div>
              </div>
            </article>
          </div>

          <div className="grid gap-6">
            <article className="business-center-os-card rounded-3xl p-5">
              <SectionHeading
                eyebrow={mode === "update" ? "Edit workspace" : "Provision workspace"}
                title={mode === "update" ? "Workspace editor" : "Workspace provisioning form"}
                description="Company scope, portal status, navigation, assignments, branding, and permission scaffolding live here."
              />
              <form className="mt-5 grid gap-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Company">
                    <select
                      value={form.companyId}
                      onChange={(event) => updateForm("companyId", event.target.value)}
                      className="business-center-os-input"
                    >
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Workspace template">
                    <select
                      value={form.workspaceKey}
                      onChange={(event) => {
                        const nextKey = event.target.value as BusinessCenterWorkspaceKey;
                        const template = templateCatalog.find((templateItem) => templateItem.key === nextKey) || templateCatalog[0];
                        const nextCompany = companies.find((company) => company.id === form.companyId) || companies[0] || null;
                        setSelectedWorkspaceId("");
                        setSelectedTemplateKey(nextKey);
                        setMode("create");
                        setForm(buildFormFromTemplate(template, nextCompany, brandingProfiles, users));
                      }}
                      className="business-center-os-input"
                    >
                      {templateCatalog.map((template) => (
                        <option key={template.id} value={template.key}>
                          {template.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Workspace name">
                    <input value={form.workspaceName} onChange={(event) => updateForm("workspaceName", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Workspace label">
                    <input value={form.workspaceLabel} onChange={(event) => updateForm("workspaceLabel", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Workspace type">
                    <select value={form.workspaceType} onChange={(event) => updateForm("workspaceType", event.target.value as BusinessCenterWorkspaceType)} className="business-center-os-input">
                      <option value="core">Core</option>
                      <option value="vertical">Vertical</option>
                      <option value="future">Future</option>
                      <option value="custom">Custom</option>
                    </select>
                  </Field>
                  <Field label="Status">
                    <select value={form.status} onChange={(event) => updateForm("status", event.target.value as BusinessCenterWorkspaceStatus)} className="business-center-os-input">
                      {workspaceStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Provisioning status">
                    <select
                      value={form.provisioningStatus}
                      onChange={(event) => updateForm("provisioningStatus", event.target.value as BusinessCenterWorkspaceProvisioningStatus)}
                      className="business-center-os-input"
                    >
                      {workspaceProvisioningStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Owner">
                    <select value={form.ownerUserId} onChange={(event) => updateForm("ownerUserId", event.target.value)} className="business-center-os-input">
                      <option value="">Unassigned</option>
                      {users
                        .filter((user) => !form.companyId || user.companyId === form.companyId)
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.fullName}
                          </option>
                        ))}
                    </select>
                  </Field>
                  <Field label="Team assignment">
                    <input value={form.teamAssignment} onChange={(event) => updateForm("teamAssignment", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Brand color">
                    <input value={form.primaryColor} onChange={(event) => updateForm("primaryColor", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Accent color">
                    <input value={form.accentColor} onChange={(event) => updateForm("accentColor", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Logo URL">
                    <input value={form.logo} onChange={(event) => updateForm("logo", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Contact email">
                    <input value={form.contactEmail} onChange={(event) => updateForm("contactEmail", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Contact phone">
                    <input value={form.contactPhone} onChange={(event) => updateForm("contactPhone", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Portal URL">
                    <input value={form.portalUrl} onChange={(event) => updateForm("portalUrl", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Domain">
                    <input value={form.domain} onChange={(event) => updateForm("domain", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Portal domain">
                    <input value={form.portalDomain} onChange={(event) => updateForm("portalDomain", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <Field label="Assigned user IDs" helper="Comma or line separated">
                  <textarea
                    value={form.assignedUserIdsText}
                    onChange={(event) => updateForm("assignedUserIdsText", event.target.value)}
                    className="business-center-os-input min-h-28"
                  />
                </Field>

                <Field label="Modules" helper="One module per line">
                  <textarea
                    value={form.modulesText}
                    onChange={(event) => updateForm("modulesText", event.target.value)}
                    className="business-center-os-input min-h-32"
                  />
                </Field>

                <Field label="Permission notes">
                  <textarea
                    value={form.permissionsNotes}
                    onChange={(event) => updateForm("permissionsNotes", event.target.value)}
                    className="business-center-os-input min-h-24"
                  />
                </Field>

                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">Role keys</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {roleOptions.map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleRole(role)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] transition ${
                            form.roleKeys.includes(role)
                              ? "border-[rgba(34,197,94,.44)] bg-[rgba(34,197,94,.12)] text-[#DCFCE7]"
                              : "border-[rgba(255,255,255,.1)] bg-white/5 text-[#C9D6DF]"
                          }`}
                        >
                          {role.replace(/_/g, " ")}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">Checklist</p>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {Object.entries(form.checklist).map(([key, value]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleChecklist(key as keyof WorkspaceFormState["checklist"])}
                          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                            value ? "border-[rgba(34,197,94,.44)] bg-[rgba(34,197,94,.08)]" : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)]"
                          }`}
                        >
                          <span className={`mt-1 inline-flex size-5 items-center justify-center rounded-full ${value ? "bg-[#22C55E]/20 text-[#86EFAC]" : "bg-white/10 text-white/60"}`}>
                            {value ? <CheckCircle2 className="size-3.5" aria-hidden="true" /> : <CircleDashed className="size-3.5" aria-hidden="true" />}
                          </span>
                          <div className="grid gap-1">
                            <span className="text-sm font-bold text-white">{formatChecklistLabel(key as keyof WorkspaceFormState["checklist"])}</span>
                            <span className="text-xs leading-5 text-[#C9D6DF]">{formatChecklistHint(key as keyof WorkspaceFormState["checklist"])}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">Feature flags</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedTemplate.defaultFeatureFlags.map((flag) => {
                        const active = form.featureFlags.some((item) => item.key === flag.key && item.enabled);
                        return (
                          <button
                            key={flag.key}
                            type="button"
                            onClick={() => toggleFeatureFlag(flag.key)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] transition ${
                              active
                                ? "border-[rgba(34,197,94,.44)] bg-[rgba(34,197,94,.12)] text-[#DCFCE7]"
                                : "border-[rgba(255,255,255,.1)] bg-white/5 text-[#C9D6DF]"
                            }`}
                          >
                            {flag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <Field label="Notes">
                  <textarea
                    value={form.notes}
                    onChange={(event) => updateForm("notes", event.target.value)}
                    className="business-center-os-input min-h-32"
                  />
                </Field>

                <div className="flex flex-wrap gap-2 pt-2">
                  <button type="submit" className="business-center-os-button inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]" disabled={saving}>
                    <BadgeCheck className="size-4" aria-hidden="true" />
                    {saving ? "Saving..." : mode === "update" ? "Save workspace" : "Provision workspace"}
                  </button>
                  {selectedWorkspace ? (
                    <>
                      <button type="button" onClick={() => void handleStatusAction("Active")} className="business-center-os-button business-center-os-button--secondary inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]">
                        Activate
                      </button>
                      <button type="button" onClick={() => void handleStatusAction("Paused")} className="business-center-os-button business-center-os-button--secondary inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]">
                        Pause
                      </button>
                      <button type="button" onClick={() => void handleStatusAction("Archived")} className="business-center-os-button business-center-os-button--secondary inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-extrabold uppercase tracking-[0.18em]">
                        Archive
                      </button>
                    </>
                  ) : null}
                </div>
              </form>
            </article>

            <article className="business-center-os-card rounded-3xl p-5">
              <SectionHeading
                eyebrow="Workspace detail"
                title={selectedWorkspace ? selectedWorkspace.workspaceLabel : "No workspace selected"}
                description="Company scope, navigation blueprint, permission scaffolding, and audit trail for the selected workspace."
              />
              {selectedWorkspace ? (
                <div className="mt-5 grid gap-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <DetailRow label="Company" value={selectedWorkspace.companyName} />
                    <DetailRow label="Template" value={selectedWorkspace.workspaceKey} />
                    <DetailRow label="Status" value={selectedWorkspace.status} />
                    <DetailRow label="Portal" value={selectedWorkspace.portalStatus} />
                    <DetailRow label="Provisioning" value={selectedWorkspace.provisioningStatus} />
                    <DetailRow label="Team assignment" value={selectedWorkspace.teamAssignment || "Unassigned"} />
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">Navigation</p>
                      <div className="mt-3 grid gap-2">
                        {selectedWorkspace.navigation.map((item) => (
                          <div key={`${item.path}-${item.label}`} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                            <div className="grid gap-0.5">
                              <span className="text-sm font-bold text-white">{item.label}</span>
                              <span className="text-xs text-[#C9D6DF]">{item.description}</span>
                            </div>
                            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#86EFAC]">{item.group}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">Assignments</p>
                      <div className="mt-3 grid gap-2">
                        {selectedWorkspace.assignments.map((assignment) => {
                          const user = users.find((item) => item.id === assignment.userId);
                          return (
                            <div key={assignment.id} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                              <div className="grid gap-0.5">
                                <span className="text-sm font-bold text-white">{user?.fullName || assignment.userId || "Unassigned"}</span>
                                <span className="text-xs text-[#C9D6DF]">{assignment.roleKey.replace(/_/g, " ")} / {assignment.assignmentType}</span>
                              </div>
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#86EFAC]">{assignment.status}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">Permission map</p>
                      <div className="mt-3 grid gap-2">
                        {selectedWorkspace.permissions.profiles.map((profile) => (
                          <div key={profile.id} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                            <span className="text-sm font-bold text-white">{profile.roleKey.replace(/_/g, " ")}</span>
                            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#86EFAC]">{countEnabledPermissions(profile.matrix)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">Checklist</p>
                      <div className="mt-3 grid gap-2">
                        {Object.entries(selectedWorkspace.checklist).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                            <span className="text-sm font-bold text-white">{formatChecklistLabel(key as keyof WorkspaceFormState["checklist"])}</span>
                            <span className={`text-[11px] font-black uppercase tracking-[0.16em] ${value ? "text-[#86EFAC]" : "text-[#FCA5A5]"}`}>{value ? "Complete" : "Pending"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">Audit trail</p>
                    <div className="mt-3 grid gap-2">
                      {selectedWorkspace.auditLog.slice().reverse().map((event) => (
                        <div key={event.id} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-bold text-white">{event.eventType}</span>
                            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#8AA0B8]">{formatDate(event.createdAt)}</span>
                          </div>
                          <p className="mt-1 text-xs leading-5 text-[#C9D6DF]">{event.summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-3xl border border-dashed border-white/10 bg-white/5 p-5 text-sm font-semibold text-[#C9D6DF]">
                  Pick a workspace from the registry or start from a template to inspect its provisioning state.
                </div>
              )}
            </article>
          </div>
        </div>
      </section>

      <section className="px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          <article className="business-center-os-card rounded-3xl p-5">
            <SectionHeading
              eyebrow="Company scope"
              title="Workspace distribution"
              description="Every workspace stays attached to one company, and every company can expand into future workspaces later."
            />
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {companies.map((company) => {
                const count = dashboard.companyCounts[company.id] || 0;
                return (
                  <div key={company.id} className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#86EFAC]">{company.brand}</p>
                    <h3 className="mt-1 text-lg font-black text-white">{company.companyName}</h3>
                    <p className="mt-1 text-sm font-semibold text-[#C9D6DF]">{count} workspace(s) provisioned</p>
                  </div>
                );
              })}
            </div>
          </article>

          <article className="business-center-os-card rounded-3xl p-5">
            <SectionHeading
              eyebrow="Compatibility"
              title="What this engine feeds"
              description="Workspace provisioning is the source of truth for onboarding, branding, portal activation, and future SaaS packaging."
            />
            <div className="mt-5 grid gap-2">
              {[
                "Client onboarding and user provisioning",
                "White-label branding and portal shells",
                "Company-scoped client portal access",
                "Role-based permissions and future enforcement",
                "Automation and workflow onboarding hooks",
                "Reporting and commercial readiness surfaces",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-[#D9E6F2]">
                  <CheckCircle2 className="mt-0.5 size-4 text-[#86EFAC]" aria-hidden="true" />
                  {item}
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      {(error || message) && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md rounded-3xl border border-[rgba(34,197,94,.2)] bg-[#08111A]/95 p-4 shadow-[0_24px_60px_rgba(0,0,0,.35)] backdrop-blur-xl">
          <p className={`text-sm font-semibold ${error ? "text-[#FCA5A5]" : "text-[#86EFAC]"}`}>{error || message}</p>
        </div>
      )}

      {loading ? (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-[#07111A]/60 backdrop-blur-sm">
          <div className="rounded-3xl border border-white/10 bg-[#0B1622] px-5 py-4 text-sm font-semibold text-[#D9E6F2] shadow-[0_24px_60px_rgba(0,0,0,.35)]">
            Loading workspace provisioning engine...
          </div>
        </div>
      ) : null}
    </BusinessCenterOSAdminShell>
  );
}

function SectionHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <div className="border-b border-[rgba(255,255,255,.08)] pb-4">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">{eyebrow}</p>
      <h2 className="mt-2 text-2xl font-black text-white">{title}</h2>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#C9D6DF]">{description}</p>
    </div>
  );
}

function MetricCard({ label, value, note }: { label: string; value: number | string; note: string }) {
  return (
    <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">{label}</p>
      <p className="mt-2 text-3xl font-black text-white">{value}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#C9D6DF]">{note}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">{label}</p>
      <p className="mt-1 text-sm font-semibold text-white">{value || "Unassigned"}</p>
    </div>
  );
}

function Field({ label, helper, children }: { label: string; helper?: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <div className="flex items-end justify-between gap-3">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">{label}</span>
        {helper ? <span className="text-[0.72rem] font-semibold text-[#74879D]">{helper}</span> : null}
      </div>
      {children}
    </label>
  );
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    document.title = `${title} | Business Center OS`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#0F1D2B");

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
    };
  }, [description, title]);
}

function createEmptyWorkspaceForm(): WorkspaceFormState {
  return {
    companyId: "",
    workspaceKey: "custom",
    workspaceName: "",
    workspaceLabel: "",
    workspaceType: "core",
    brand: "",
    status: "Draft",
    provisioningStatus: "Not Started",
    portalStatus: "draft",
    ownerUserId: "",
    assignedUserIdsText: "",
    teamAssignment: "",
    modulesText: "",
    primaryColor: "#1c69d4",
    accentColor: "#14b8a6",
    logo: "",
    contactEmail: "",
    contactPhone: "",
    domain: "",
    portalDomain: "",
    portalUrl: "",
    permissionsNotes: "",
    roleKeys: ["viewer"],
    featureFlags: [],
    navigation: [],
    checklist: defaultChecklist,
    notes: "",
  };
}

function buildFormFromWorkspace(workspace: BusinessCenterWorkspaceRecord): WorkspaceFormState {
  return {
    companyId: workspace.companyId,
    workspaceKey: workspace.workspaceKey,
    workspaceName: workspace.workspaceName,
    workspaceLabel: workspace.workspaceLabel,
    workspaceType: workspace.workspaceType,
    brand: workspace.brand,
    status: workspace.status,
    provisioningStatus: workspace.provisioningStatus,
    portalStatus: workspace.portalStatus,
    ownerUserId: workspace.ownerUserId || "",
    assignedUserIdsText: workspace.assignedUserIds.join("\n"),
    teamAssignment: workspace.teamAssignment,
    modulesText: workspace.modules.join("\n"),
    primaryColor: workspace.branding.primaryColor,
    accentColor: workspace.branding.accentColor,
    logo: workspace.branding.logo,
    contactEmail: workspace.branding.contactEmail,
    contactPhone: workspace.branding.contactPhone,
    domain: workspace.branding.domain,
    portalDomain: workspace.branding.portalDomain,
    portalUrl: workspace.portalUrl,
    permissionsNotes: workspace.permissions.notes,
    roleKeys: workspace.permissions.roleKeys.length > 0 ? workspace.permissions.roleKeys : ["viewer"],
    featureFlags: workspace.featureFlags,
    navigation: workspace.navigation,
    checklist: workspace.checklist,
    notes: workspace.notes,
  };
}

function buildFormFromTemplate(
  template: ReturnType<typeof loadBusinessCenterWorkspaceTemplates>[number],
  company: BusinessCenterCompanyRecord | null,
  brandingProfiles: ReturnType<typeof loadBusinessCenterBrandingProfiles>,
  users: BusinessCenterUserRecord[],
): WorkspaceFormState {
  const nextCompany = company || null;
  const branding = nextCompany
    ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)
    : brandingProfiles[0] || null;
  return {
    companyId: nextCompany?.id || "",
    workspaceKey: template.key,
    workspaceName: template.label,
    workspaceLabel: template.label,
    workspaceType: template.workspaceType,
    brand: nextCompany?.brand || template.brand,
    status: nextCompany?.companyType === "parent" ? "Active" : "Provisioning",
    provisioningStatus: nextCompany?.companyType === "parent" ? "Complete" : "In Progress",
    portalStatus: nextCompany?.companyType === "parent" ? "active" : "pending",
    ownerUserId: users.find((user) => !nextCompany || user.companyId === nextCompany.id)?.id || "",
    assignedUserIdsText: users.filter((user) => !nextCompany || user.companyId === nextCompany.id).map((user) => user.id).join("\n"),
    teamAssignment: template.workspaceType === "core" ? "Governance" : template.label,
    modulesText: template.defaultModules.join("\n"),
    primaryColor: nextCompany ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)?.primaryColor || template.primaryColor : template.primaryColor,
    accentColor: nextCompany ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)?.accentColor || template.accentColor : template.accentColor,
    logo: nextCompany ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)?.logoUrl || "" : "",
    contactEmail: nextCompany ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)?.contactEmail || `hello@${nextCompany.companySlug}.com` : "hello@example.com",
    contactPhone: nextCompany ? resolveBusinessCenterBrandingProfile(nextCompany.id, nextCompany.companySlug, nextCompany.companyName, nextCompany.brand)?.phoneNumber || "" : "",
    domain: nextCompany ? `${nextCompany.companySlug}.com` : "",
    portalDomain: nextCompany ? `portal.${nextCompany.companySlug}.com` : "",
    portalUrl: nextCompany ? buildPortalUrl(nextCompany.companySlug, template.key) : "",
    permissionsNotes: "Default role matrix aligned to Business Center OS security rules.",
    roleKeys: template.defaultRoleKeys,
    featureFlags: template.defaultFeatureFlags,
    navigation: template.defaultNavigation,
    checklist: defaultChecklist,
    notes: nextCompany ? `${template.label} provisioned for ${nextCompany.companyName}.` : template.description,
  };
}

function buildPortalUrl(companySlug: string, workspaceKey: BusinessCenterWorkspaceKey) {
  const params = new URLSearchParams({
    company: companySlug,
    workspace: workspaceKey,
  });
  return `/client/portal?${params.toString()}`;
}

function splitList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,]/g)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

function countEnabledPermissions(matrix: BusinessCenterPermissionMatrix) {
  const values = Object.values(matrix).flatMap((entry) => Object.values(entry));
  return `${values.filter(Boolean).length} enabled`;
}

function formatChecklistLabel(key: keyof WorkspaceFormState["checklist"]) {
  return {
    companyLinked: "Company linked",
    brandingMapped: "Branding mapped",
    portalActivated: "Portal activated",
    navigationLoaded: "Navigation loaded",
    permissionsMapped: "Permissions mapped",
    usersAssigned: "Users assigned",
    reportingEnabled: "Reporting enabled",
  }[key];
}

function formatChecklistHint(key: keyof WorkspaceFormState["checklist"]) {
  return {
    companyLinked: "Workspace is tied to the correct company scope.",
    brandingMapped: "Colors, logo, and contact data are ready.",
    portalActivated: "Client portal can be activated for this workspace.",
    navigationLoaded: "Workspace navigation blueprint is present.",
    permissionsMapped: "Role mapping exists for company-scoped access.",
    usersAssigned: "Users are attached to the workspace.",
    reportingEnabled: "Reporting surfaces can be extended from here.",
  }[key];
}
